import { useState } from "react";
import { useNavigate } from "react-router";
import { CalendarPlus, LayoutGrid, Users } from "lucide-react";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";
import { BuscarPacienteDialog } from "@/components/patients/BuscarPacienteDialog";
import { usePermissao } from "@/lib/use-permissao";
import { useIsMobile } from "@/lib/use-media-query";
import { useNovaConsulta } from "@/components/agenda/use-nova-consulta";
import { useFluxoNovoPedido } from "@/components/prosthesis/FluxoNovoPedido";

/** Empilhado no mobile (ícone em cima, rótulo em duas linhas) e lado a lado no desktop. Empilhar
 *  os três em coluna cheia custava 246px — 37% da tela num iPhone 13 — e jogava a agenda pra fora
 *  da primeira dobra. O rótulo quebra sozinho no espaço ("Cadastrar" / "Paciente"), então nada de
 *  <br> forçado. */
const actionCls =
  "flex flex-col items-center gap-1.5 rounded-xl border border-zinc-200 bg-white p-3 text-center shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/50 disabled:opacity-60 sm:flex-row sm:gap-3 sm:p-4 sm:text-left dark:border-border dark:bg-card dark:hover:bg-primary/5";

const iconWrapCls =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10";

const labelCls =
  "text-[11px] font-semibold leading-tight text-zinc-900 dark:text-foreground sm:text-base sm:leading-normal";

function QuickAction({
  label,
  icon,
  tone,
  dataTour,
  onClick,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  /** Classes de cor do quadrado do ícone — cada atalho tem a sua. */
  tone: string;
  dataTour: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" data-tour={dataTour} onClick={onClick} disabled={disabled} className={actionCls}>
      <span className={`${iconWrapCls} ${tone}`}>{icon}</span>
      <span className={labelCls}>{label}</span>
    </button>
  );
}

const iconCls = "h-4 w-4 sm:h-5 sm:w-5";

/** Atalhos do dia a dia da clínica. "Pacientes" acha e abre a ficha — cadastrar ficou dentro
 *  dele (quando a busca não acha) e no Agendar consulta, onde o paciente novo de fato aparece. */
export function QuickActions() {
  const navigate = useNavigate();
  const [patientDialogOpen, setPatientDialogOpen] = useState(false);
  const podeVerPacientes = usePermissao("patients.view");
  // No celular Menu e Pacientes já estão na barra de abas — ficam só as duas ações.
  const isMobile = useIsMobile();
  const consulta = useNovaConsulta();
  const pedido = useFluxoNovoPedido({ onConcluido: () => navigate("/protese") });

  return (
    <>
      {/* Quatro numa linha no desktop; no celular só Agendar e Pedir, lado a lado. */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {!isMobile && (
          <QuickAction
            label="Menu"
            dataTour="atalho-menu"
            tone="bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400"
            icon={<LayoutGrid className={iconCls} />}
            onClick={() => navigate("/menu")}
          />
        )}
        {podeVerPacientes && !isMobile && (
          <QuickAction
            label="Pacientes"
            dataTour="cadastrar-paciente"
            tone="bg-brand-100 text-brand-700 dark:bg-primary/15 dark:text-primary"
            icon={<Users className={iconCls} />}
            onClick={() => setPatientDialogOpen(true)}
          />
        )}
        <QuickAction
          label="Agendar consulta"
          dataTour="agendar-consulta"
          tone="bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400"
          icon={<CalendarPlus className={iconCls} />}
          onClick={consulta.abrir}
          disabled={!consulta.pronto}
        />
        <QuickAction
          label="Pedir prótese"
          dataTour="pedir-protese"
          tone="bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/15 dark:text-fuchsia-400"
          icon={<CoroaDentariaIcon className={iconCls} />}
          onClick={pedido.abrirNovo}
        />
      </div>

      <BuscarPacienteDialog open={patientDialogOpen} onClose={() => setPatientDialogOpen(false)} />

      {consulta.elementos}
      {pedido.elementos}
    </>
  );
}
